import React, { useEffect, useRef, useState } from "react";
import styles from "./GenerateInvoicePanel.module.css";
import WebService from "../Services/WebService";
import { toast } from "react-toastify";
import CrossButton from "../Customs/Button/CrossButton";

type Props = {
  open: boolean;
  orderId: number;
  isCustomer: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  finalAmount?: number;
  onOpenAcquireRawMaterial?: () => void;
  requiresRawMaterialAcquisition?: boolean;
};

const GenerateInvoicePanel: React.FC<Props> = ({
  open,
  orderId,
  isCustomer,
  onClose,
  onSuccess,
  onOpenAcquireRawMaterial,
  requiresRawMaterialAcquisition,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const warningRef = useRef<HTMLDivElement>(null);

  // Loaded from order detail
  const [finalAmount, setFinalAmount] = useState<number | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderRequiresAcquisition, setOrderRequiresAcquisition] = useState(true);

  // Form fields
  const [additionalCharges, setAdditionalCharges] = useState<string>("");
  const [additionalDiscount, setAdditionalDiscount] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [showWarning, setShowWarning] = useState(false);

 
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      // If warning is open, allow interactions inside it without closing the panel
      if (showWarning && warningRef.current && warningRef.current.contains(e.target as Node)) {
        return;
      }
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);

  // Fetch order to get final_amount for preview
  useEffect(() => {
    if (!open || !orderId) return;

    const fetchFinalAmount = async () => {
      try {
        setLoadingOrder(true);
        const base = isCustomer
          ? "/v1/door-management/saleorders/my-saleorders"
          : "/v1/door-management/saleorders";
        const res: any = await WebService.getAPI({ action: `${base}/${orderId}` });
        const data = res?.data || res; // tolerate different shapes
        const amt =
          typeof data?.final_amount === "number"
            ? data.final_amount
            : typeof data?.data?.final_amount === "number"
            ? data.data.final_amount
            : null;
        setFinalAmount(amt);

        const items = data?.sale_order_items || data?.data?.sale_order_items || [];
        const flag = data?.requires_raw_material_acquisition ?? data?.data?.requires_raw_material_acquisition;
        if (typeof flag === "boolean") {
          setOrderRequiresAcquisition(flag);
        } else {
          setOrderRequiresAcquisition(
            (Array.isArray(items) ? items : []).some(
              (item: any) => String(item?.product_type || "PRODUCT").toUpperCase() !== "RAW_MATERIAL"
            )
          );
        }
      } catch {
        setFinalAmount(null);
      } finally {
        setLoadingOrder(false);
      }
    };

    fetchFinalAmount();
  }, [open, orderId, isCustomer]);

  // Validation
  const validate = () => {
    const e: Record<string, string> = {};
    if (!notes.trim()) e.notes = "Notes are required";
    if (additionalCharges !== "" && Number(additionalCharges) < 0) {
      e.additionalCharges = "Additional charges cannot be negative";
    }
    if (additionalDiscount !== "" && Number(additionalDiscount) < 0) {
      e.additionalDiscount = "Additional discount cannot be negative";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Currency fmt
  const inr = (n: number) =>
    "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Live preview math
  const base = typeof finalAmount === "number" ? finalAmount : 0;
  const addChg = additionalCharges ? Number(additionalCharges) : 0;
  const addDisc = additionalDiscount ? Number(additionalDiscount) : 0;
  const afterCharges = base + addChg;
  const payable = Math.max(0, afterCharges - addDisc);

  // Check if raw materials are acquired
  const checkAcquiredMaterials = async (): Promise<boolean> => {
    try {
      const res: any = await WebService.getAPI({
        action: `/v1/door-management/saleorders/acquire-raw-materials/${orderId}`,
      });
      
      // Check if no materials acquired
      if (res?.status_code === 200 && res?.message === "No acquired raw materials found") {
        return false;
      }
      
      // Check if data is empty array
      if (res?.status_code === 200 && Array.isArray(res?.data) && res.data.length === 0) {
        return false;
      }
      
      return true;
    } catch {
      // If API fails, allow to proceed (don't block on API error)
      return true;
    }
  };

  // Submit (unchanged API body)
  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    // Product lines need acquired production materials; sold raw-material lines do not.
    const mustAcquire =
      typeof requiresRawMaterialAcquisition === "boolean"
        ? requiresRawMaterialAcquisition
        : orderRequiresAcquisition;
    if (mustAcquire) {
      const hasMaterials = await checkAcquiredMaterials();
      if (!hasMaterials) {
        setShowWarning(true);
        return;
      }
    }

    await proceedWithGeneration();
  };

  const proceedWithGeneration = async () => {

    try {
      setSubmitting(true);
      const basePath = isCustomer
        ? "/v1/door-management/saleorders/my-saleorders"
        : "/v1/door-management/saleorders";

      const body: any = { notes };
      if (additionalCharges !== "") body.additional_charges = Number(additionalCharges);
      if (additionalDiscount !== "") body.additional_discount = Number(additionalDiscount);

      const res: any = await WebService.postAPI({
        action: `${basePath}/${orderId}/generate-invoice`,
        body,
      });

      toast.success(
        res?.data?.invoice?.invoice_number
          ? `Invoice ${res.data.invoice.invoice_number} generated!`
          : res?.data?.invoice_number
          ? `Invoice ${res.data.invoice_number} generated!`
          : res?.message || "Invoice generated!"
      );
      onClose();
      onSuccess?.();
    } catch {
      // handled globally by WebService.errorHandler
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcquireRedirect = () => {
    setShowWarning(false);
    onClose();
    // Use setTimeout to ensure invoice panel closes before opening acquire panel
    setTimeout(() => {
      console.log("[InvoicePanel] Triggering onOpenAcquireRawMaterial from warning button", {
        hasCallback: Boolean(onOpenAcquireRawMaterial),
      });
      onOpenAcquireRawMaterial?.();
    }, 100);
  };

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.panel} ref={panelRef}>
        <div className={styles.header}>
          <div>Generate Invoice</div>
          <CrossButton onClick={onClose} className={styles.closeBtn} />
        </div>

        <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
          {/* 1) Total Amount (read-only) */}
          <div className={styles.group}>
            <label>Total Amount </label>
            <input
              className={styles.input}
              value={
                loadingOrder
                  ? "Loading..."
                  : typeof finalAmount === "number"
                  ? inr(finalAmount)
                  : "—"
              }
              readOnly
              disabled
            />
          </div>
          <div className={styles.group}>
            <label>Additional Charges (₹)</label>
            <input
              className={`${styles.input} ${errors.additionalCharges ? styles.error : ""}`}
              type="number"
              min="0"
              step="0.01"
              placeholder="Amount (₹)"
              value={additionalCharges}
              onChange={(e) => setAdditionalCharges(e.target.value)}
            />
            {errors.additionalCharges && (
              <div className={styles.errMsg}>{errors.additionalCharges}</div>
            )}
          </div>

          {/* 3) Additional Discount */}
          <div className={styles.group}>
            <label>Additional Discount (₹)</label>
            <input
              className={`${styles.input} ${errors.additionalDiscount ? styles.error : ""}`}
              type="number"
              min="0"
              step="0.01"
              placeholder="Discount in Rupees (₹)"
              value={additionalDiscount}
              onChange={(e) => setAdditionalDiscount(e.target.value)}
            />
            {errors.additionalDiscount && (
              <div className={styles.errMsg}>{errors.additionalDiscount}</div>
            )}
          </div>

          {/* 4) Notes */}
          <div className={styles.group}>
            <label>Notes*</label>
            <textarea
              className={`${styles.input} ${styles.textarea} ${errors.notes ? styles.error : ""}`}
              placeholder="Write notes for the invoice"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={250}
            />
            {errors.notes && <div className={styles.errMsg}>{errors.notes}</div>}
          </div>

          {/* 5) Preview */}
          <div className={styles.group}>
            <label>Preview</label>
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: "12px 14px",
                background: "#fafafa",
                fontWeight: 600,
                lineHeight: 1.6,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Total Amount</span>
                <span>{typeof finalAmount === "number" ? inr(base) : "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>+ Additional Charges</span>
                <span>{inr(addChg)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>− Additional Discount</span>
                <span>{inr(addDisc)}</span>
              </div>
              <hr style={{ margin: "10px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 16,
                }}
              >
                <span>Payable</span>
                <span>{inr(payable)}</span>
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? "Generating..." : "Generate"}
            </button>
          </div>
        </form>
      </div>

      {/* Warning Modal for No Raw Materials */}
      {showWarning && (
        <div className={styles.warningOverlay} onClick={() => setShowWarning(false)}>
          <div
            className={styles.warningModal}
            ref={warningRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.warningIcon}>⚠️</div>
            <h3 className={styles.warningTitle}>No Raw Materials Acquired</h3>
            <p className={styles.warningText}>
              No raw materials have been acquired for this sale order. Please acquire raw materials before generating the invoice to proceed further.
            </p>
            <div className={styles.warningActions}>
              <button
                type="button"
                className={styles.warningBtn}
                onClick={handleAcquireRedirect}
              >
                Acquire now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateInvoicePanel;
